"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.SelectablePill = void 0;
require("./SelectablePill.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _conditionalAttributes = require("../../util/conditionalAttributes/conditionalAttributes");
var _Button = _interopRequireDefault(require("../Button/Button"));
var _Icon = _interopRequireDefault(require("../Icon/Icon"));
var _Link = _interopRequireDefault(require("../Link/Link"));
var _Text = _interopRequireDefault(require("../Text/Text"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/**
 * SelectablePill component
 *
 * @method
 * @param { SelectablePillProps } props
 * @returns SelectablePill
 */

const SelectablePill = props => {
  const {
    actionType = defaultProps.actionType,
    active = defaultProps.active,
    ariaDescribedBy,
    disabled = defaultProps.disabled,
    dismissibleIcon = defaultProps.dismissibleIcon,
    externalPillAction,
    iconName,
    message,
    ref,
    screenReaderText,
    tabIndex,
    unavailable = defaultProps.unavailable,
    variant = defaultProps.variant,
    ...rest
  } = props;
  const isFilterAndActive = variant === 'filter' && active && !unavailable && !disabled;
  const isButton = actionType === 'button';
  const isLink = actionType === 'link';
  const pillContent = /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, iconName && /*#__PURE__*/_react.default.createElement(_Icon.default, {
    className: "pal-c-SelectablePill--icon",
    size: "lg",
    name: iconName
  }), /*#__PURE__*/_react.default.createElement(_Text.default, {
    className: "pal-c-SelectablePill--text",
    htmlTag: "span",
    textStyle: "label"
  }, message), isFilterAndActive && /*#__PURE__*/_react.default.createElement(_Icon.default, {
    className: "pal-c-SelectablePill--dismissibleIcon",
    name: dismissibleIcon
  }));
  const conditionalPropsButton = (0, _conditionalAttributes.conditionalAttributes)({
    'aria-describedby': ariaDescribedBy || undefined,
    'aria-disabled': disabled || undefined,
    'aria-pressed': active || undefined
  });
  const conditionalPropsLink = (0, _conditionalAttributes.conditionalAttributes)({
    'aria-current': active ? 'true' : 'false',
    'aria-describedby': ariaDescribedBy || undefined,
    'aria-disabled': disabled || undefined
  });
  const componentClassName = (0, _classnames.default)('pal-c-SelectablePill', {
    'pal-c-SelectablePill--active': active && !disabled,
    'pal-c-SelectablePill--disabled': disabled,
    'pal-c-SelectablePill--disabledActive': active && disabled,
    'pal-c-SelectablePill--unavailable': unavailable,
    'pal-c-SelectablePill--unavailableActive': active && unavailable,
    [`pal-c-SelectablePill--${variant}`]: variant
  });
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, isButton && /*#__PURE__*/_react.default.createElement(_Button.default, _extends({
    buttonRef: ref,
    className: componentClassName,
    disabled: disabled,
    onClick: externalPillAction,
    screenReaderText: screenReaderText,
    tabIndex: tabIndex,
    variant: "unstyled"
  }, conditionalPropsButton, rest), pillContent), isLink && /*#__PURE__*/_react.default.createElement(_Link.default, _extends({
    linkRef: ref,
    className: componentClassName,
    disabled: disabled,
    onClick: externalPillAction,
    screenReaderText: screenReaderText,
    tabIndex: tabIndex,
    variant: "unstyled"
  }, conditionalPropsLink, rest), pillContent));
};

/**
 * Property type definitions
 * @typedef SelectablePillProps
 * @type {object}
 * @property {string} actionType - Sets the type of action ( button or link )
 * @property {boolean} active - Sets the selected state for button/link checked state
 * @property {string} ariaDescribedBy - sets the aria-describedby attribute for button or input elements
 * @property {boolean} disabled - Sets the disabled state
 * @property {string} dismissibleIcon - Sets the dismissible icon for the active filter pill
 * @property {func} externalPillAction - Sets the onClick for button or link
 * @property {string} iconName - Sets the icon for the pill
 * @property {string} message - Sets the message to be displayed in the pill
 * @property {any} ref - Sets the ref of the selectable card
 * @property {string} screenReaderText - Sets the screenReaderText for the pill
 * @property {number} tabIndex - Sets the tabIndex for the button or input elements
 * @property {boolean} unavailable - Sets the unavailable state for button/link checked state
 * @property {string} variant - Sets the variant of the SelectablePill
 */
exports.SelectablePill = SelectablePill;
const propTypes = exports.propTypes = {
  /**
   * Sets the type of action ( button or link )
   */
  actionType: _propTypes.default.oneOf(['button', 'link']),
  /**
   * Sets the active state for button/link checked state
   */
  active: _propTypes.default.bool,
  /**
   * Sets the aria-describedby attribute for button or input elements
   */
  ariaDescribedBy: _propTypes.default.string,
  /**
   * Sets the disabled state
   */
  disabled: _propTypes.default.bool,
  /**
   * Sets the dismissible icon for the active filter pill
   */
  dismissibleIcon: _propTypes.default.string,
  /**
   * Sets the onClick for button or link
   */
  externalPillAction: _propTypes.default.func,
  /**
   * Sets the icon for the pill
   */
  iconName: _propTypes.default.string,
  /**
   * Sets the message to be displayed in the pill
   */
  message: _propTypes.default.string,
  /**
   * pill reference
   */
  ref: _propTypes.default.any,
  /**
   * Sets the screenReaderText for the pill
   */
  screenReaderText: _propTypes.default.string,
  /**
   * Sets the tab-index of the button or input element
   */
  tabIndex: _propTypes.default.number,
  /**
   * Sets the unavailable state for button/link checked state
   */
  unavailable: _propTypes.default.bool,
  /**
   * Sets the variant of the SelectablePill
   */
  variant: _propTypes.default.oneOf(['default', 'filter'])
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} actionType='button' - Sets the type of action to button
 * @property {boolean} active='false' - Sets active to false
 * @property {boolean} disabled='false' - Sets the disabled state to false
 * @property {string} dismissibleIcon='x' - Sets the dismissible icon to x
 * @property {boolean} unavailable='false' - Sets the unavailable state for button/link checked state to false
 * @property {string} variant - Sets the variant of the SelectablePill to default
 */
const defaultProps = exports.defaultProps = {
  actionType: 'button',
  active: false,
  disabled: false,
  dismissibleIcon: 'x',
  unavailable: false,
  variant: 'default'
};
SelectablePill.propTypes = propTypes;
var _default = exports.default = SelectablePill;